import React, { useState } from "react";

import { Edi277ClaimStatus } from "../types";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import { CollapsibleSection } from "../CollapsibleSection";
import { formatCurrency, formatEntityName } from "../viewerHelpers";
import Field from "./Field";
import StatusBadge from "./StatusBadge";
import TabButton from "./TabButton";
import ExpandCollapseToggle from "./ExpandCollapseToggle";
import TabBarWithArrows from "./TabBarWithArrows";
import StatusInfoDisplay from "./StatusInfoDisplay";

const ClaimStatus277Viewer: React.FC<{ status: Edi277ClaimStatus }> = ({
  status,
}) => {
  const [activeTab, setActiveTab] = useState<string>("status");
  const [allExpanded, setAllExpanded] = useState(true);

  const hasRejection = status.statusInfos?.some(
    (s) => s.actionType === "REJECT",
  );

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="bg-white px-4 py-3">
        <div className="flex items-center flex-wrap gap-2">
          <StatusBadge status="CLAIM_STATUS" type="type" />
          {hasRejection && (
            <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-sm font-medium">
              REJECTED
            </span>
          )}
          <div className="flex-1" />
          <div>
            <span className="text-gray-500 text-sm">CLAIM ID</span>
            <span className="ml-2 font-medium text-sm">
              {status.patientControlNumber || "-"}
            </span>
          </div>
          {status.payerClaimControlNumber && (
            <div>
              <span className="text-gray-500 text-sm">PAYER CTRL #</span>
              <span className="ml-2 font-medium text-sm">
                {status.payerClaimControlNumber}
              </span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <TabBarWithArrows>
          <TabButton
            active={activeTab === "status"}
            onClick={() => setActiveTab("status")}
          >
            Status Info
          </TabButton>
          <TabButton
            active={activeTab === "lines"}
            onClick={() => setActiveTab("lines")}
            count={status.lineStatuses?.length}
          >
            Line Statuses
          </TabButton>
          <TabButton
            active={activeTab === "details"}
            onClick={() => setActiveTab("details")}
          >
            Details
          </TabButton>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <ExpandCollapseToggle
              expanded={allExpanded}
              onToggle={setAllExpanded}
            />
          </div>
        </TabBarWithArrows>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 scrollbar-thin">
        {activeTab === "status" && (
          <div>
            <CollapsibleSection title="Claim Status" defaultOpen={allExpanded}>
              {status.statusInfos?.map((info, idx) => (
                <StatusInfoDisplay key={idx} statusInfo={info} />
              )) || (
                <div className="text-gray-500 text-sm">
                  No status information
                </div>
              )}
            </CollapsibleSection>

            {status.receiverBatchStatus && (
              <CollapsibleSection
                title="Receiver Batch Status"
                defaultOpen={allExpanded}
              >
                <div
                  className="grid gap-4 mb-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  }}
                >
                  <Field
                    label="Accepted Qty"
                    value={status.receiverBatchStatus.acceptedQuantity}
                  />
                  <Field
                    label="Accepted Amt"
                    value={formatCurrency(
                      status.receiverBatchStatus.acceptedAmount,
                    )}
                  />
                  <Field
                    label="Rejected Qty"
                    value={status.receiverBatchStatus.rejectedQuantity}
                  />
                  <Field
                    label="Rejected Amt"
                    value={formatCurrency(
                      status.receiverBatchStatus.rejectedAmount,
                    )}
                  />
                </div>
                {status.receiverBatchStatus.statusInfos?.map((info, idx) => (
                  <StatusInfoDisplay key={idx} statusInfo={info} />
                ))}
              </CollapsibleSection>
            )}

            {status.providerBatchStatus && (
              <CollapsibleSection
                title="Provider Batch Status"
                defaultOpen={allExpanded}
              >
                <div
                  className="grid gap-4 mb-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  }}
                >
                  <Field
                    label="Accepted Qty"
                    value={status.providerBatchStatus.acceptedQuantity}
                  />
                  <Field
                    label="Accepted Amt"
                    value={formatCurrency(
                      status.providerBatchStatus.acceptedAmount,
                    )}
                  />
                  <Field
                    label="Rejected Qty"
                    value={status.providerBatchStatus.rejectedQuantity}
                  />
                  <Field
                    label="Rejected Amt"
                    value={formatCurrency(
                      status.providerBatchStatus.rejectedAmount,
                    )}
                  />
                </div>
                {status.providerBatchStatus.statusInfos?.map((info, idx) => (
                  <StatusInfoDisplay key={idx} statusInfo={info} />
                ))}
              </CollapsibleSection>
            )}
          </div>
        )}

        {activeTab === "lines" && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-sm text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left">Line</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Service Date</th>
                    <th className="px-3 py-2 text-right">Charge</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {status.lineStatuses?.map((line, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-md font-medium">
                        {line.controlNumber || idx + 1}
                      </td>
                      <td className="px-3 py-2 text-md">
                        {line.procedure?.code || line.revenueCode?.code || "-"}
                      </td>
                      <td className="px-3 py-2 text-md text-gray-600">
                        {formatDisplayDate(line.serviceDateFrom)}
                      </td>
                      <td className="px-3 py-2 text-md text-right">
                        {formatCurrency(line.chargeAmount)}
                      </td>
                      <td className="px-3 py-2 text-md">
                        {line.statusInfos?.map((si, i) => (
                          <span
                            key={i}
                            className={`inline-block px-1.5 py-0.5 rounded text-sm mr-1 ${
                              si.actionType === "REJECT"
                                ? "bg-red-100 text-red-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {si.actionType || si.actionCode}
                          </span>
                        ))}
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No line statuses
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "details" && (
          <div>
            <CollapsibleSection
              title="Claim Reference"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Patient Control Number"
                  value={status.patientControlNumber}
                />
                <Field
                  label="Payer Claim Control #"
                  value={status.payerClaimControlNumber}
                />
                <Field
                  label="Clearinghouse Trace #"
                  value={status.clearinghouseTraceNumber}
                />
                <Field label="Bill Type Code" value={status.billTypeCode} />
                <Field
                  label="Service Date From"
                  value={formatDisplayDate(status.serviceDateFrom)}
                />
                <Field
                  label="Service Date To"
                  value={formatDisplayDate(status.serviceDateTo)}
                />
              </div>
            </CollapsibleSection>

            {status.patient && (
              <CollapsibleSection title="Patient" defaultOpen={allExpanded}>
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Name"
                    value={formatEntityName(status.patient)}
                  />
                  <Field label="ID" value={status.patient.identifier} />
                </div>
              </CollapsibleSection>
            )}

            {status.provider && (
              <CollapsibleSection title="Provider" defaultOpen={allExpanded}>
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Name"
                    value={status.provider.lastNameOrOrgName}
                  />
                  <Field label="NPI" value={status.provider.identifier} />
                </div>
              </CollapsibleSection>
            )}

            {status.receiver && (
              <CollapsibleSection title="Receiver" defaultOpen={allExpanded}>
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Name"
                    value={status.receiver.lastNameOrOrgName}
                  />
                  <Field label="ID" value={status.receiver.identifier} />
                </div>
              </CollapsibleSection>
            )}

            {status.transaction && (
              <CollapsibleSection title="Transaction" defaultOpen={allExpanded}>
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  <Field
                    label="Control Number"
                    value={status.transaction.controlNumber}
                  />
                  <Field
                    label="Transaction Set"
                    value={status.transaction.transactionSetIdentifierCode}
                  />
                  <Field
                    label="Creation Date"
                    value={formatDisplayDate(status.transaction.creationDate)}
                  />
                </div>
              </CollapsibleSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimStatus277Viewer;
